import {
    AllEvents,
    AppState,
    AuthenticationState,
    BreakingNewsCard,
    createCardClickedEvent,
    createCollectionAvailableEvent,
    entitledToAll,
    useFeature,
} from '@news-mono/web-common'
import React from 'react'
import { useSelector } from 'react-redux'
import { useImpressionAvailable } from '../../__helpers/impression-available-helper'
import { useProduct } from '../../__product'
import {
    StyledBreakingNews,
    StyledBreakingNewsTitle,
    StyledIconChevronRight,
    StyledPromoStrapMark,
    StyledWrapper,
} from '../BreakingNews/BreakingNews.styled'
import { BlackFridayStrap } from '../BlackFridayStrap/BlackFridayStrap'
import { useAuthActions } from '../../user-registration'
import { FootyLivesHerePromoStrap } from '../FootyLivesHerePromoStrap/FootyLivesHerePromoStrap'

export type PromoStrapStyle = 'default' | 'black-friday' | 'footy-lives-here'

export interface PromoStrapProps {
    titlePrefix: string
    unsubTitlePrefix: string
    link: string | undefined
    unsubLink: string | undefined
    theme: PromoStrapStyle
    onEvent: (event: AllEvents) => void
}

export interface PromoStrapBanner {
    title: string
    link: string | undefined
    onEvent: (event: AllEvents) => void
}

export const PromoStrap = (props: PromoStrapProps) => {
    const { titlePrefix, unsubTitlePrefix, link, unsubLink, onEvent, theme } =
        props
    const product = useProduct()
    const hideOnMobileAndApp = useFeature('hide-mobile-breaking-news-banner')
    const { getSubscribeLink } = useAuthActions()
    const authentication = useSelector<AppState, AuthenticationState>(
        ({ authentication }) => authentication,
    )
    const isEntitled = entitledToAll(authentication)
    const isVisible = isPromoStrapVisible(theme, isEntitled)

    const cardFormat = 'BreakingNews'
    let title: string
    let bannerLink: string | undefined

    if (isEntitled) {
        title = titlePrefix
        bannerLink = link
    } else {
        title = unsubTitlePrefix
        bannerLink = unsubLink
    }

    const newItem: BreakingNewsCard = {
        shortHeadline: title,
        link: link ? link : '',
        cardType: 'breaking-news',
        listId: 'promo-strap',
    }
    const linkClicked = () => {
        onEvent(
            createCardClickedEvent(
                onEvent,
                newItem,
                'PromoStrap',
                cardFormat,
                1,
            ),
        )
    }
    const impressionAvailableFunction = () => {
        if (!isVisible) {
            return
        }

        onEvent(
            createCollectionAvailableEvent(
                [`Promo Strap: ${title}`],
                `Promo Strap`,
                product,
                onEvent,
                {
                    cardFormat: 'PromoStrap',
                },
            ),
        )
    }

    const impressionRef = useImpressionAvailable({
        loading: false,
        available: impressionAvailableFunction,
    })

    // Determine which promo strap to show
    if (title && bannerLink && authentication.hasAttemptedValidation) {
        switch (theme) {
            case 'black-friday':
                return !isEntitled ? (
                    <BlackFridayStrap
                        bannerLink={getSubscribeLink()}
                        onEvent={onEvent}
                    />
                ) : null
            case 'footy-lives-here':
                return !isEntitled ? (
                    <FootyLivesHerePromoStrap
                        bannerLink={bannerLink}
                        onEvent={onEvent}
                        text={title}
                        impressionRef={impressionRef}
                    />
                ) : null
        }

        // Return the default promo strap style
        return (
            <div ref={impressionRef}>
                <StyledBreakingNews
                    to={bannerLink ? bannerLink : ''}
                    target={'_blank'}
                    onClick={linkClicked}
                    breakingNewsTheme="promo-strap"
                    hideOnMobileAndApp={hideOnMobileAndApp}
                >
                    <StyledWrapper>
                        <StyledPromoStrapMark />
                        <StyledBreakingNewsTitle
                            breakingNewsTheme="promo-strap"
                            fontScale={1}
                        >
                            {`${title}`}
                        </StyledBreakingNewsTitle>
                    </StyledWrapper>
                    <StyledIconChevronRight />
                </StyledBreakingNews>
            </div>
        )
    }

    // If no title or link is provided, return null
    return null
}
PromoStrap.displayName = 'PromoStrap'

const isPromoStrapVisible = (theme: PromoStrapStyle, isEntitled: boolean) => {
    switch (theme) {
        case 'black-friday':
        case 'footy-lives-here':
            return !isEntitled
        default:
            return true
    }
}
